import { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import queryString from "query-string";
import includes from "lodash/fp/includes";
import { setAppTheme, updateCommonSearchParams } from "features/booking/bookingSlice";
import { selectCurrentThemeId, getThemeById } from "utils/themeUtils";
import Routes from "config/routes";
import { BOOKING_THEMES } from "utils/constants";

/**
 * Custom hook to manage application theming
 * Consolidates theme logic and provides the current theme object
 */

export const useThemeManager = (history) => {
  const dispatch = useDispatch();

  // Redux state
  const currentThemeId = useSelector(selectCurrentThemeId);
  const hasReservationInProgress =
    useSelector((state) => state.booking.roomReservations).length > 0;
  const hasBookingConfirmation = useSelector((state) =>
    Boolean(state.booking.bookingConfirmationId),
  );

  // Route checks
  const isOnPlanStay = includes(Routes.PLAN_STAY)(history.location.pathname);
  const isOnChooseRoom = includes(Routes.CHOOSE_ROOM)(history.location.pathname);

  // Memoized theme object
  const currentTheme = useMemo(() => {
    return getThemeById(currentThemeId);
  }, [currentThemeId]);

  // Update theme when dependencies change
  useEffect(() => {
    const determineTheme = () => {
      const searchParams = queryString.parse(history.location.search);

      // Handle redirects with bookingTheme parameter
      if (searchParams.bookingTheme && typeof searchParams.bookingTheme === "string") {
        const sourceValue = searchParams.bookingTheme.toLowerCase();

        // Find matching bookingTheme by ID
        const matchingTheme = Object.values(BOOKING_THEMES).find(
          (source) => source.id?.toLowerCase() === sourceValue,
        );

        if (matchingTheme) {
          return matchingTheme.id;
        }
      }

      // Handle Redirect from soho
      if (isOnChooseRoom && searchParams.bookingTheme === BOOKING_THEMES.SOHO.id) {
        return BOOKING_THEMES.SOHO.id;
      }

      // Handle Redirect from Mollies.com
      if (isOnChooseRoom && !searchParams.bookingTheme && !hasReservationInProgress) {
        return BOOKING_THEMES.DEFAULT.id;
      }

      // Handle Refresh on other pages of Booking Flow including Multi-Room Booking Flow
      if (
        currentThemeId === BOOKING_THEMES.SOHO.id &&
        !isOnPlanStay &&
        (hasReservationInProgress || hasBookingConfirmation)
      ) {
        return BOOKING_THEMES.SOHO.id;
      }

      // Handle other specific edge cases
      if (
        currentThemeId &&
        currentThemeId !== BOOKING_THEMES.DEFAULT.id &&
        !isOnPlanStay &&
        (hasReservationInProgress || hasBookingConfirmation)
      ) {
        return currentThemeId;
      }

      return BOOKING_THEMES.DEFAULT.id;
    };

    const requiredTheme = determineTheme();

    if (currentThemeId !== requiredTheme) {
      dispatch(setAppTheme(requiredTheme));
      // Use specific action to avoid race conditions with other hooks
      dispatch(updateCommonSearchParams({ bookingTheme: requiredTheme }));
    }
  }, [
    history.location.pathname,
    history.location.search,
    hasReservationInProgress,
    hasBookingConfirmation,
    currentThemeId,
    dispatch,
  ]);

  return {
    currentTheme,
    currentThemeId,
  };
};

export default useThemeManager;
